import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auditService, AuditSearchFilters, AuditConfig } from '../../services/auditService';
import { useToast } from '../../../components/Toast';

export const useAuditQuery = (filters: AuditSearchFilters) => {
  return useQuery({
    queryKey: ['audit', filters],
    queryFn: () => auditService.searchAuditLogs(filters),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });
};

export const useAuditExport = () => {
  const { showToast } = useToast();
  
  return useMutation({
    mutationFn: (filters: AuditSearchFilters) => auditService.exportAuditLogs(filters),
    onSuccess: (data) => {
      // Trigger download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      showToast('Audit logs exported successfully', 'success');
    },
    onError: () => {
      showToast('Failed to export audit logs', 'error');
    }
  });
};

export const useAuditStats = (days: number = 30) => {
  return useQuery({
    queryKey: ['audit-stats', days],
    queryFn: () => auditService.getAuditStats(days),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useAuditConfig = () => {
  return useQuery({
    queryKey: ['audit-config'],
    queryFn: () => auditService.getAuditConfig(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useUpdateAuditConfig = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  
  return useMutation({
    mutationFn: ({ resourceType, config }: { resourceType: string; config: Partial<AuditConfig> }) => 
      auditService.updateAuditConfig(resourceType, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audit-config'] });
      showToast('Audit configuration updated', 'success');
    },
    onError: () => {
      showToast('Failed to update audit configuration', 'error');
    }
  });
};

export const useCleanupAuditLogs = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  
  return useMutation({
    mutationFn: () => auditService.cleanupAuditLogs(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['audit'] });
      showToast(`Cleaned up ${data.deletedCount} expired audit logs`, 'success');
    },
    onError: () => {
      showToast('Failed to cleanup audit logs', 'error');
    }
  });
};